export const INCH_TO_CM = 2.54;
export const CM_TO_INC = 1 / INCH_TO_CM;
export const LB_TO_KG = 0.453592;
export const KG_TO_LB = 1 / LB_TO_KG;

export function isNumber(something: unknown): something is number {
  return typeof something === 'number' && !isNaN(something);
}
export function isNaturalNumber(something: unknown): something is number {
  return Number.isInteger(something) && something >= 0;
}
export function isPositiveInteger(something: unknown): something is number {
  return Number.isInteger(something) && something > 0;
}
export function isPositiveNumber(something: unknown): something is number {
  return isNumber(something) && something > 0;
}
export function isPercentage(something: unknown): something is number {
  return isNaturalNumber(something) && something <= 100;
}
export function isPositivePercentage(something: unknown): something is number {
  return isPositiveInteger(something) && something <= 100;
}

export type Rational = [number, number, number];
export function toRoundedRational(number: Maybe<string | number>): Rational {
  // We're rounding up to the next 1/8
  const roundedRatio = Math.ceil((parseFloat(number) || 0) * 8) / 8;
  const whole = Math.floor(roundedRatio);
  const remainder = roundedRatio % 1;
  let numerator = remainder,
    denominator = 1;
  while (!Number.isInteger(numerator)) {
    numerator = 2 * numerator;
    denominator = 2 * denominator;
  }
  return [whole, numerator, denominator];
}
export function toRoundedRationalString(number: Maybe<string | number>): string {
  if (!number) return '0';
  const isNegative = parseFloat(number) < 0;
  const [who, num, den] = toRoundedRational(isNegative ? -number : number);
  return `${isNegative ? '-' : ''}${[who, num && `${num}/${den}`].filter(Boolean).join(' ')}`;
}
export function toRoundedRationalNumber(number: Maybe<string | number>): number {
  const [whole, numerator, denominator] = toRoundedRational(number);
  return whole + numerator / denominator;
}
export function getClosestFraction(value: number): string {
  const decimalParts = value.toString().split('.');
  const decimalPlaces =
    decimalParts.length === 1 ? 0 : decimalParts[decimalParts.length - 1].length;
  const gcdVal = gcd(value * Math.pow(10, decimalPlaces), Math.pow(10, decimalPlaces));
  const numerator = (value * Math.pow(10, decimalPlaces)) / gcdVal;
  const denominator = Math.pow(10, decimalPlaces) / gcdVal;
  return `${numerator}/${denominator}`;
}
export function gcd(a: number, b: number): number {
  return !b ? a : gcd(b, a % b);
}
export function lcm(a: number, b: number): number {
  return (a * b) / gcd(a, b);
}
export function toNextEigth(size: number): number {
  let n = 0;
  while (size > n / 8) n++;
  return n / 8;
}
export function maybeAdd(a: Maybe<number>, b: Maybe<number>): number {
  return (a || 0) + (b || 0);
}
export function maybeAddGroup(...nums: Maybe<number>[]): number {
  return nums.reduce((acc, num) => maybeAdd(acc, num), 0) as number;
}
export function maybeSub(a: Maybe<number>, b: Maybe<number>): number {
  return (a || 0) - (b || 0);
}
/**
 * Subtracts all numbers in the group from the first number
 */
export function maybeSubGroup(...nums: Maybe<number>[]): number {
  return nums.reduce((acc, num) => maybeSub(acc, num)) as number;
}
export function maybeMul(a: Maybe<number>, b: Maybe<number>): number {
  return (a || 0) * (b || 0);
}
export function maybeMulGroup(...nums: Maybe<number>[]): number {
  return nums.reduce((acc, num) => maybeMul(acc, num), 1) as number;
}
export function maybeDiv(a: Maybe<number>, b: Maybe<number>): number {
  return (a || 0) / (b || 1);
}
/**
 * Divides the first number by every number in the group
 */
export function maybeDivGroup(...nums: Maybe<number>[]): number {
  return nums.reduce((acc, num) => maybeDiv(acc, num)) as number;
}
export function maybeGt(a: Maybe<number>, b: Maybe<number>): boolean {
  return (a || 0) > (b || 0);
}
export function maybeLt(a: Maybe<number>, b: Maybe<number>): boolean {
  return (a || 0) < (b || 0);
}
export function average(nums: number[]) {
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}
export function standardDeviation(nums: number[]) {
  const avg = average(nums);
  return Math.sqrt(average(nums.map(num => Math.pow(num - avg, 2))));
}
export function getDataZScore(value: number, avg: number, stdd: number) {
  if (stdd === 0) {
    return NaN;
  }
  return Math.abs((value - avg) / stdd);
}
export function betterReadableBigNumbers(value: number, decimalPlaces?: number): string {
  if (value / 1_000_000_000 >= 1) {
    return `${(value / 1_000_000_000).toFixed(decimalPlaces || 0)}B`;
  }
  if (value / 1_000_000 >= 1) {
    return `${(value / 1_000_000).toFixed(decimalPlaces || 0)}M`;
  }
  if (value / 1_000 >= 1) {
    return `${(value / 1_000).toFixed(decimalPlaces || 0)}K`;
  }
  return value.toString();
}
